/**
 * Account closure queue.
 *
 * Requests from two places that look the same in a table and are not the same job. An in-app
 * request arrived from inside a signed-in session, so the person is already proven and the
 * only question is whether closing them now is safe. A public-form request arrived from a URL
 * anybody can open, so the first question is whether they are who they say they are — and if
 * we cannot even find the account, there is nothing to approve.
 *
 * The segments are those jobs, in the order they get done: what is waiting, what is waiting
 * AND unidentified, and what has been approved but is still inside the window where support
 * can take it back.
 *
 * Approving is the only action in this console that deactivates a live account, so it is the
 * only one that shows what the account still has in flight before you press it.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import {
  CalendarClock,
  Globe,
  MoreHorizontal,
  RotateCcw,
  Search,
  ShieldQuestion,
  Smartphone,
  Store,
  Trash2,
  UserRound,
} from 'lucide-react';
import {
  enum_param,
  page_param,
  page_size_param,
  sort_param,
  string_param,
  use_url_state,
} from '@/hooks/use-url-state';
import { DashboardLayout, PageTabs } from '@/components/dashboard-layout';
import { DataTable } from '@/components/data-table/data-table';
import { FilterBar, SearchInput, SelectFilter, type ChipInput } from '@/components/filters/filter-bar';
import { DateRangeFilter, describe_range } from '@/components/filters/date-range-filter';
import { MetricStrip, type Metric } from '@/components/metric-strip';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { notify } from '@/components/ui/toaster';
import { ago, date_time, short_id } from '@/lib/format';
import {
  use_deletion_counts,
  use_deletion_list,
} from '@/features/account-deletions/hooks';
import {
  DELETION_SOURCES,
  DELETION_STATUSES,
  DELETION_SUBJECT_TYPES,
  ACCOUNT_DELETION_SORT_FIELDS,
  days_until_purge,
  in_grace_period,
  is_unidentified,
  status_tone,
  type AccountDeletionCounts,
  type AccountDeletionFilters,
  type AccountDeletionRequest,
} from '@/features/account-deletions/types';
import { export_deletions } from '@/features/account-deletions/api';
import { DeletionDecisionSheet } from './decision-sheet';

const SEGMENTS = ['pending', 'unidentified', 'grace', 'decided', 'all'] as const;
type Segment = (typeof SEGMENTS)[number];

const SEGMENT_FILTERS: Record<Segment, Partial<AccountDeletionFilters>> = {
  pending: { status: 'PENDING' },
  // Waiting AND we do not know whose account it is. A different job from the rest of
  // `pending`: this one starts with finding the person, not with deciding about them.
  unidentified: { status: 'PENDING', matched: 'false' },
  grace: { status: 'APPROVED' },
  decided: { status: 'REJECTED' },
  all: {},
};

const SEGMENT_COPY: Record<Segment, { label: string; title: string; blurb: string }> = {
  pending: {
    label: 'Waiting',
    title: 'Closure requests waiting',
    blurb: 'People who have asked to have their account deleted',
  },
  unidentified: {
    label: 'Unidentified',
    title: 'Requests we cannot trace',
    blurb: 'Public-form requests that match no account — find the person before deciding',
  },
  grace: {
    title: 'Approved and counting down',
    label: 'In grace period',
    blurb: 'Closed accounts still inside the window where support can reverse it',
  },
  decided: { label: 'Refused', title: 'Refused requests', blurb: 'Requests we did not approve' },
  all: { label: 'All', title: 'Account deletion requests', blurb: 'Every request ever raised' },
};

export function AccountDeletionListPage() {
  const [segment_raw, set_segment] = use_url_state('segment', enum_param(SEGMENTS));
  const segment: Segment = segment_raw || 'pending';
  const copy = SEGMENT_COPY[segment];

  const [q, set_q] = use_url_state('q', string_param);
  const [subject_type, set_subject_type] = use_url_state(
    'subject_type',
    enum_param(DELETION_SUBJECT_TYPES),
  );
  const [source, set_source] = use_url_state('source', enum_param(DELETION_SOURCES));
  const [status, set_status] = use_url_state('status', enum_param(DELETION_STATUSES));
  const [from, set_from] = use_url_state('from', string_param);
  const [to, set_to] = use_url_state('to', string_param);
  const [page, set_page] = use_url_state('page', page_param);
  const [page_size, set_page_size] = use_url_state('page_size', page_size_param);
  const [sort, set_sort] = use_url_state(
    'sort',
    sort_param(ACCOUNT_DELETION_SORT_FIELDS, { by: 'created_at', dir: 'asc' }),
  );

  /** The request open in the decision panel. */
  const [open_id, set_open_id] = useState<string | null>(null);

  const reset_page = () => set_page(1);

  const filters: AccountDeletionFilters = useMemo(
    () => ({
      ...SEGMENT_FILTERS[segment],
      page,
      page_size,
      q: q || undefined,
      subject_type: subject_type || undefined,
      source: source || undefined,
      // A hand-picked status beats the segment's, except on the segments whose whole
      // identity IS a status — picking "rejected" inside "Waiting" would empty the tab.
      ...(status && (segment === 'all' || segment === 'decided') ? { status } : {}),
      from: from || undefined,
      to: to || undefined,
    }),
    [segment, page, page_size, q, subject_type, source, status, from, to],
  );

  const { data, isLoading, isFetching, error, refetch } = use_deletion_list(filters, sort);
  const { data: counts } = use_deletion_counts();

  const chips: ChipInput[] = [
    q && { key: 'q', label: 'Search', value: q, on_clear: () => set_q('') },
    subject_type && {
      key: 'subject_type',
      label: 'Account',
      value: subject_type === 'VENDOR' ? 'Vendor' : 'Pet owner',
      on_clear: () => set_subject_type(''),
    },
    source && {
      key: 'source',
      label: 'Came from',
      value: source === 'PUBLIC_FORM' ? 'Public page' : 'In the app',
      on_clear: () => set_source(''),
    },
    status &&
      (segment === 'all' || segment === 'decided') && {
        key: 'status',
        label: 'Status',
        value: status,
        on_clear: () => set_status(''),
      },
    (from || to) && {
      key: 'date',
      label: 'Raised',
      value: describe_range(from, to),
      on_clear: () => {
        set_from('');
        set_to('');
      },
    },
  ];

  function clear_all() {
    set_q('');
    set_subject_type('');
    set_source('');
    set_status('');
    set_from('');
    set_to('');
    reset_page();
  }

  const metrics: Metric[] = useMemo(() => build_metrics(counts), [counts]);

  const columns = useMemo<ColumnDef<AccountDeletionRequest>[]>(
    () => [
      {
        id: 'requester',
        header: 'Who',
        enableSorting: false,
        meta: {
          label: 'Requester',
          width: '18rem',
          csv: (r) => r.contact_name ?? r.contact_email ?? r.contact_phone ?? r.id,
        },
        cell: ({ row }) => {
          const r = row.original;
          const name = r.contact_name ?? r.business_name ?? 'Name not given';
          return (
            <span className="flex min-w-0 flex-col">
              <span className="truncate font-medium" title={name}>
                {name}
              </span>
              <span className="truncate text-micro text-muted-foreground">
                {r.contact_phone ?? r.contact_email ?? short_id(r.id)}
              </span>
            </span>
          );
        },
      },
      {
        id: 'account',
        header: 'Account',
        enableSorting: false,
        meta: {
          label: 'Account',
          width: '12rem',
          csv: (r) => `${r.subject_type}${r.user_id ? '' : ' (unmatched)'}`,
        },
        cell: ({ row }) => {
          const r = row.original;
          /*
           * The distinction the whole queue turns on. An unmatched request cannot be
           * approved at all — the API refuses it — so saying so in the row saves an
           * operator opening it to find out.
           */
          if (is_unidentified(r)) {
            return (
              <Badge tone="warning" size="sm" dot data-testid={`unmatched-${r.id}`}>
                <ShieldQuestion className="h-3 w-3" /> No account found
              </Badge>
            );
          }
          return (
            <span className="flex min-w-0 flex-col gap-0.5">
              {r.subject_type === 'VENDOR' ? (
                <Link
                  to={`/vendors/${r.vendor_profile_id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-center gap-1.5 truncate font-medium underline-offset-2 hover:text-primary hover:underline"
                >
                  <Store className="h-3.5 w-3.5 shrink-0" />
                  {r.business_name ?? 'Vendor'}
                </Link>
              ) : (
                <Link
                  to={`/users/${r.user_id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-center gap-1.5 truncate font-medium underline-offset-2 hover:text-primary hover:underline"
                >
                  <UserRound className="h-3.5 w-3.5 shrink-0" /> Pet owner
                </Link>
              )}
              {r.account_is_active === false && (
                <span className="text-micro text-muted-foreground">Closed</span>
              )}
            </span>
          );
        },
      },
      {
        id: 'source',
        header: 'Came from',
        enableSorting: false,
        meta: { label: 'Source', width: '9rem', csv: (r) => r.source },
        cell: ({ row }) => {
          const r = row.original;
          return r.source === 'PUBLIC_FORM' ? (
            <Badge tone="info" size="sm" variant="outline">
              <Globe className="h-3 w-3" /> Public page
            </Badge>
          ) : (
            <Badge tone="neutral" size="sm" variant="outline">
              <Smartphone className="h-3 w-3" /> In the app
            </Badge>
          );
        },
      },
      {
        id: 'raised',
        header: 'Raised',
        meta: { sort_key: 'created_at', label: 'Raised', width: '9rem', csv: (r) => r.created_at },
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-data" title={date_time(row.original.created_at)}>
            {ago(row.original.created_at)}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        meta: {
          sort_key: 'status',
          label: 'Status',
          width: '12rem',
          csv: (r) => r.status,
        },
        cell: ({ row }) => {
          const r = row.original;
          if (in_grace_period(r)) {
            const days = days_until_purge(r);
            return (
              <span className="flex min-w-0 flex-col gap-0.5">
                <Badge tone="destructive" size="sm" dot data-testid={`status-${r.id}`}>
                  Closed
                </Badge>
                {/* The number that matters: after this, cancelling stops working. */}
                <span className="whitespace-nowrap text-micro text-muted-foreground">
                  Erased in {days} day{days === 1 ? '' : 's'}
                </span>
              </span>
            );
          }
          return (
            <Badge tone={status_tone(r)} size="sm" dot data-testid={`status-${r.id}`}>
              {r.status === 'PENDING'
                ? 'Waiting'
                : r.status === 'APPROVED'
                  ? 'Erased'
                  : r.status === 'REJECTED'
                    ? 'Refused'
                    : 'Withdrawn'}
            </Badge>
          );
        },
      },
      {
        id: 'reason',
        header: 'Why',
        enableSorting: false,
        meta: { label: 'Reason', width: '18rem', csv: (r) => r.reason ?? '' },
        cell: ({ row }) => {
          const r = row.original;
          return r.reason ? (
            <span className="line-clamp-2 text-data" title={r.reason}>
              {r.reason}
            </span>
          ) : (
            <span className="text-data text-muted-foreground/60">No reason given</span>
          );
        },
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        meta: { align: 'right', width: '1%' },
        cell: ({ row }) => {
          const r = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-control text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label="Request actions"
                  data-testid={`row-actions-${r.id}`}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <RowAction on_select={() => set_open_id(r.id)} testid={`review-${r.id}`}>
                  <Search className="h-4 w-4" /> Review it
                </RowAction>
                {r.user_id && (
                  <>
                    <DropdownMenuSeparator />
                    <RowAction
                      on_select={() =>
                        window.open(
                          r.subject_type === 'VENDOR'
                            ? `/vendors/${r.vendor_profile_id}`
                            : `/users/${r.user_id}`,
                          '_self',
                        )
                      }
                      testid={`open-account-${r.id}`}
                    >
                      {r.subject_type === 'VENDOR' ? (
                        <>
                          <Store className="h-4 w-4" /> Open the vendor
                        </>
                      ) : (
                        <>
                          <UserRound className="h-4 w-4" /> Open the account
                        </>
                      )}
                    </RowAction>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [],
  );

  return (
    <DashboardLayout
      title={copy.title}
      subtitle={copy.blurb}
      tabs={
        <PageTabs
          current={segment}
          on_change={(v) => {
            set_segment(v as Segment);
            reset_page();
          }}
          tabs={SEGMENTS.map((v) => ({
            value: v,
            label: SEGMENT_COPY[v].label,
            count:
              v === 'pending'
                ? counts?.pending
                : v === 'unidentified'
                  ? counts?.pending_public
                  : v === 'grace'
                    ? counts?.in_grace_period
                    : v === 'decided'
                      ? counts?.rejected
                      : undefined,
          }))}
        />
      }
      stats={<MetricStrip metrics={metrics} />}
    >
      <DataTable<AccountDeletionRequest>
        columns={columns}
        data={data?.items ?? []}
        total={data?.total ?? 0}
        get_row_id={(r) => r.id}
        page={page}
        page_size={page_size}
        on_page_change={set_page}
        on_page_size_change={(n) => {
          set_page_size(n);
          reset_page();
        }}
        sort={sort}
        on_sort_change={(next) => {
          set_sort(next);
          reset_page();
        }}
        is_loading={isLoading}
        is_fetching={isFetching}
        error={error}
        on_retry={() => void refetch()}
        // Opens the decision panel rather than navigating: the decision needs the row's
        // context beside it, and a queue you have to leave to action is a queue you lose
        // your place in.
        row_actions={undefined}
        storage_key={`account-deletions:${segment}`}
        export_name="account-deletions"
        on_export_all={() => void export_deletions(filters, sort)}
        caption="Account deletion requests"
        test_id="account-deletion-list"
        empty={{
          icon: Trash2,
          title: chips.some(Boolean)
            ? 'No requests match these filters'
            : segment === 'pending'
              ? 'Nothing waiting'
              : segment === 'unidentified'
                ? 'Nothing untraced'
                : segment === 'grace'
                  ? 'No closures counting down'
                  : 'No requests',
          description:
            segment === 'pending'
              ? 'Every closure request has been dealt with.'
              : segment === 'unidentified'
                ? 'Every public request so far has matched an account.'
                : segment === 'grace'
                  ? 'Nothing is inside the window where a closure can still be reversed.'
                  : 'Requests appear here as people ask to close their accounts.',
        }}
        filters={
          <FilterBar chips={chips} on_clear_all={clear_all}>
            <SearchInput
              value={q}
              on_change={(next) => {
                set_q(next);
                reset_page();
              }}
              label="Search"
              placeholder="Name, phone or email…"
              test_id="filter-q"
            />
            <SelectFilter
              label="Account"
              value={subject_type}
              on_change={(v) => {
                set_subject_type(v);
                reset_page();
              }}
              options={[
                { value: 'USER', label: 'Pet owner' },
                { value: 'VENDOR', label: 'Vendor' },
              ]}
              all_label="Either"
              test_id="filter-subject-type"
            />
            <SelectFilter
              label="Came from"
              value={source}
              on_change={(v) => {
                set_source(v);
                reset_page();
              }}
              options={[
                { value: 'PUBLIC_FORM', label: 'Public page' },
                { value: 'IN_APP', label: 'In the app' },
              ]}
              all_label="Either"
              test_id="filter-source"
            />
            {(segment === 'all' || segment === 'decided') && (
              <SelectFilter
                label="Status"
                value={status}
                on_change={(v) => {
                  set_status(v);
                  reset_page();
                }}
                options={DELETION_STATUSES.map((s) => ({ value: s, label: s }))}
                all_label="Any"
                test_id="filter-status"
              />
            )}
            <DateRangeFilter
              from={from}
              to={to}
              on_change={(next) => {
                set_from(next.from);
                set_to(next.to);
                reset_page();
              }}
              label="Raised"
            />
          </FilterBar>
        }
        row_href={undefined}
      />

      <DeletionDecisionSheet
        id={open_id}
        on_close={() => set_open_id(null)}
        on_done={(message) => {
          set_open_id(null);
          notify.success(message);
        }}
      />
    </DashboardLayout>
  );
}

function build_metrics(counts: AccountDeletionCounts | undefined): Metric[] {
  return [
    {
      key: 'pending',
      label: 'Waiting on us',
      value: counts?.pending?.toLocaleString('en-IN'),
      hint:
        counts?.oldest_pending_hours != null
          ? `Oldest has waited ${counts.oldest_pending_hours}h`
          : 'Nothing outstanding',
      tone: counts && counts.pending > 0 ? 'warning' : 'default',
      to: '/account-deletions?segment=pending',
      icon: CalendarClock,
      loading: !counts,
    },
    {
      key: 'public',
      label: 'From the public page',
      value: counts?.pending_public?.toLocaleString('en-IN'),
      hint: 'Identity not proven yet',
      tone: counts && counts.pending_public > 0 ? 'warning' : 'default',
      to: '/account-deletions?segment=unidentified',
      icon: Globe,
      loading: !counts,
    },
    {
      key: 'grace',
      /*
       * The one number on this page with a deadline attached. After the window closes the
       * data is gone and the cancel button stops working, so an operator wants to see it
       * without going looking.
       */
      label: 'Counting down',
      value: counts?.in_grace_period?.toLocaleString('en-IN'),
      hint: 'Closed, still reversible',
      tone: counts && counts.in_grace_period > 0 ? 'destructive' : 'default',
      to: '/account-deletions?segment=grace',
      icon: RotateCcw,
      loading: !counts,
    },
    {
      key: 'vendors',
      label: 'Vendors leaving',
      value: counts?.pending_vendors?.toLocaleString('en-IN'),
      hint: 'Businesses waiting on a decision',
      icon: Store,
      loading: !counts,
    },
    {
      key: 'total',
      label: 'Requests',
      value: counts?.total?.toLocaleString('en-IN'),
      hint: counts ? `${counts.rejected} refused` : undefined,
      to: '/account-deletions?segment=all',
      icon: Trash2,
      loading: !counts,
    },
  ];
}

/** See the reviews list for why Radix menu items need pointer handlers. */
function RowAction({
  children,
  on_select,
  testid,
}: {
  children: React.ReactNode;
  on_select: () => void;
  testid: string;
}) {
  return (
    <DropdownMenuItem
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      onSelect={on_select}
      data-testid={testid}
    >
      {children}
    </DropdownMenuItem>
  );
}
